import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { AttachmentList } from '@/components/finance/AttachmentList';
import { SubmissionForm } from '@/components/finance/SubmissionForm';
import { errorMessage } from '@/lib/errors';
import { financeMySubmissionsQuery, financeSubmissionRefsQuery } from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { formatMinor } from '@/lib/money';
import { FINANCE_EXPECTED_STATUS_TONE } from '@/lib/status-tone';
import type { FinanceExpected } from '@/api/generated/types.gen';

export const Route = createFileRoute('/_app/finance/submissions')({ component: SubmissionsPage });

const LIMIT = 50;

function SubmissionsPage() {
  return <SubmissionsScreen />;
}

// Thin route glue only, same reasoning as finance.queue.tsx's own QueuePage/
// QueueScreen split — every real behavior, permission gate included, lives
// in SubmissionsScreen so it is testable with plain props/mocks and no
// matched router route.
export function SubmissionsScreen() {
  const { can } = usePermissions();
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<FinanceExpected | null>(null);

  // ONLY these two queries, both gated server-side on finance.submit (spec
  // §16 item 12 / rulings.md #32: finance.submit exists without finance.read
  // — getFinanceSubmissionRefs and listMyFinanceSubmissions are the two
  // endpoints written for exactly that actor). Reaching for
  // financeCategoriesQuery/financeAccountsQuery/apartmentsQuery here for
  // "just the names" would 403 a submitter-only actor on a page they are
  // otherwise fully entitled to use.
  const mine = useQuery(financeMySubmissionsQuery({ limit: LIMIT, offset: page * LIMIT }));
  const refs = useQuery(financeSubmissionRefsQuery);

  const items = mine.data?.items ?? [];
  const total = mine.data?.total ?? 0;

  usePageHeader({ subtitle: mine.data ? `${total} submissions` : undefined });

  if (!can('finance.submit') || errorStatus(mine.error) === 403) return <AccessDenied />;

  const categoryNames = Object.fromEntries((refs.data?.categories ?? []).map((c) => [c.id, c.name]));
  const hasPrev = page > 0;
  const hasNext = (page + 1) * LIMIT < total;

  return (
    <div className="flex flex-col gap-4">
      {mine.isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : mine.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(mine.error)}
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-8 text-center text-sm text-muted">
          No submissions yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((e) => {
            const tone = FINANCE_EXPECTED_STATUS_TONE[e.status];
            return (
              <li
                key={e.id}
                className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-foreground">
                      {categoryNames[e.category_id] ?? e.category_id}
                    </p>
                    <p className="text-sm text-muted">
                      {formatMinor(e.amount_minor, e.currency)} · due {e.due_date}
                    </p>
                    {e.status === 'skipped' && e.skip_reason && (
                      <p className="text-sm text-muted">Skipped — {e.skip_reason}</p>
                    )}
                  </div>
                  <Badge variant={tone.variant} dot>
                    {tone.label}
                  </Badge>
                </div>
                <AttachmentList items={e.attachments ?? []} />
                {e.status === 'draft' && (
                  <div>
                    <Button variant="secondary" size="sm" onClick={() => setEditing(e)}>
                      Edit draft
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {items.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted">
          <span>
            Showing {total === 0 ? 0 : page * LIMIT + 1}–{page * LIMIT + items.length} of {total}
          </span>
          <span className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={!hasPrev} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="secondary" size="sm" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </span>
        </div>
      )}

      {editing && refs.data && (
        <Dialog open onClose={() => setEditing(null)} side title="Edit draft">
          <SubmissionForm
            refs={refs.data}
            initial={editing}
            initialAttachments={editing.attachments}
            onDone={(submission) => {
              setEditing(null);
              toast({
                kind: 'success',
                title: submission.status === 'draft' ? 'Draft saved' : 'Submission sent',
              });
            }}
            onCancel={() => setEditing(null)}
          />
        </Dialog>
      )}
    </div>
  );
}
